import React from 'react'
import i18n from 'config/i18n'
import { GridRenderCellParams } from '@mui/x-data-grid-pro'
import { parseBackendDate, getDurationInDaysAndHours, getDurationByDays } from 'services/dates'
import { REGISTRATION_STATUS_ENUM } from 'services/api/registrations'
import { StringCell } from 'components/Shared/MuiTable/Cell/StringCell'
import { PLATFORM_ENUM } from 'services/api/constants'
import { CampaignState } from 'state/campaigns'
import { theme } from 'theme'
import { PartiposterProfile, SocialMediaAccount } from 'state/submissions'
import { TagCell } from 'components/Shared/MuiTable/Cell/TagCell'
import { RegistrationResultState } from 'state/registrations'
import { getLanguageCode } from 'config/i18n'
import { TIMESTAMP_KEY, DeclinedRegistrationResultsTableRows, GridColDefProps } from './constants'
import { showDefaultIgPlatform } from 'components/utils/campaign'
import { ProfileCell } from 'components/Shared/MuiTable/Cell/ProfileCell'
import { SocialMediaInfoCell } from 'components/Shared/MuiTable/Cell/SocialMediaInfoCell'
import { FIELDS_TYPE } from 'components/utils/filterAndSort'

const TIMESTAMP_TITLE = {
    [REGISTRATION_STATUS_ENUM.AWAITING_RESPONSE]: i18n.t('submission.table.approvedDateLabel'),
    [REGISTRATION_STATUS_ENUM.ACCEPTED_INVITES]: i18n.t('submission.table.acceptedDateLabel'),
    [REGISTRATION_STATUS_ENUM.JOINED_CAMPAIGN]: i18n.t('submission.table.acceptedDateLabel'),
    [REGISTRATION_STATUS_ENUM.DECLINED_INVITES]: i18n.t('submission.table.declinedInviteDateLabel'),
}

export const getColumns = (
    status: REGISTRATION_STATUS_ENUM,
    campaign: CampaignState,
    searchablePlatforms: PLATFORM_ENUM[],
    canManageTag: boolean,
    followerRequirementsPlatform: PLATFORM_ENUM[],
    updateViewedRow?: (id: string) => void,
): GridColDefProps[] => {
    if (status === REGISTRATION_STATUS_ENUM.PENDING) return []
    if (!campaign) return []
    const { requirements } = campaign
    const hasRequirements = requirements.length > 0
    const hasPlatform = (platform: PLATFORM_ENUM) => {
        return searchablePlatforms.includes(platform) || followerRequirementsPlatform.includes(platform)
    }
    const cols: GridColDefProps[] = [
        {
            field: 'profile',
            type: 'string',
            headerName: i18n.t('submission.table.profile'),
            width: 160,
            minWidth: 60,
            sortable: false,
            renderCell: (props: GridRenderCellParams) => <ProfileCell {...props} />,
            subFields: [
                {
                    field: 'name',
                    headerName: i18n.t('submission.table.nameLabel'),
                },
                {
                    field: 'gender',
                    headerName: i18n.t('submission.table.genderLabel'),
                },
                {
                    field: 'age',
                    headerName: i18n.t('submission.table.ageLabel'),
                },
                ...(hasRequirements
                    ? [
                          {
                              field: 'meetRequirement',
                              headerName: i18n.t('submission.table.meetRequirementsLabel'),
                          },
                      ]
                    : []),
            ],
        },
        ...(status === REGISTRATION_STATUS_ENUM.AWAITING_RESPONSE
            ? [
                  {
                      field: 'approval_expired_at',
                      type: 'string',
                      headerName: i18n.t('submission.table.registrationExpiredDateLabel'),
                      width: 200,
                      renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      sortable: false,
                      filterable: false,
                  },
              ]
            : []),
        {
            field: 'socialMediaInfo',
            type: 'string',
            headerName: i18n.t('submission.table.socialMediaInfo'),
            width: 180,
            minWidth: 60,
            sortable: false,
            renderCell: (props: GridRenderCellParams) => {
                const handleUpdateViewed = () => {
                    if (updateViewedRow) {
                        updateViewedRow(props.row.uuid)
                    }
                }
                return <SocialMediaInfoCell {...props} onClick={handleUpdateViewed} />
            },
            subFields: [
                ...(showDefaultIgPlatform(searchablePlatforms, followerRequirementsPlatform)
                    ? [
                          {
                              field: FIELDS_TYPE.IG_FOLLOWERS,
                              headerName: i18n.t('submission.table.instagramFollowersLabel'),
                          },
                          {
                              field: FIELDS_TYPE.IG_USERNAME,
                              headerName: i18n.t('submission.table.instagramUsernameLabel'),
                          },
                      ]
                    : []),
                ...(hasPlatform(PLATFORM_ENUM.FB)
                    ? [
                          {
                              field: FIELDS_TYPE.FB_FRIENDS,
                              headerName: i18n.t('submission.table.facebookFriendsLabel'),
                          },
                          {
                              field: FIELDS_TYPE.FB_USERNAME,
                              headerName: i18n.t('submission.table.facebookUsernameLabel'),
                          },
                      ]
                    : []),
                ...(hasPlatform(PLATFORM_ENUM.TIKTOK)
                    ? [
                          {
                              field: FIELDS_TYPE.TIKTOK_FOLLOWERS,
                              headerName: i18n.t('submission.table.tikTokFollowersLabel'),
                          },
                          {
                              field: FIELDS_TYPE.TIKTOK_USERNAME,
                              headerName: i18n.t('submission.table.tikTokUsernameLabel'),
                          },
                      ]
                    : []),
                ...(hasPlatform(PLATFORM_ENUM.TWITTER)
                    ? [
                          {
                              field: FIELDS_TYPE.TWITTER_FOLLOWERS,
                              headerName: i18n.t('submission.table.twitterFollowersLabel'),
                          },
                          {
                              field: FIELDS_TYPE.TWITTER_USERNAME,
                              headerName: i18n.t('submission.table.twitterUsernameLabel'),
                          },
                      ]
                    : []),
            ],
        },
        ...(status != REGISTRATION_STATUS_ENUM.WAITLISTED
            ? [
                  {
                      field: TIMESTAMP_KEY[status],
                      headerName: TIMESTAMP_TITLE[status],
                      minWidth: 192,
                      renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      sortable: false,
                      filterable: false,
                  },
              ]
            : []),
    ]

    return cols.concat([
        ...(status === REGISTRATION_STATUS_ENUM.JOINED_CAMPAIGN
            ? [
                  {
                      field: 'first_content_submitted_at',
                      headerName: i18n.t('submission.table.startedTaskDateLabel'),
                      width: 190,
                      renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      sortable: false,
                      filterable: false,
                  },
              ]
            : []),
        {
            field: 'lastReviewedBy',
            type: 'string',
            headerName: i18n.t('submission.table.lastReviewedLabel'),
            minWidth: 180,
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
            sortable: false,
            filterable: false,
        },
        {
            field: 'lastReviewedAt',
            type: 'string',
            headerName: i18n.t('submission.table.lastReviewedDateLabel'),
            minWidth: 180,
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
            sortable: false,
            filterable: false,
        },
        ...(canManageTag
            ? [
                  {
                      field: 'partiposterTags',
                      headerName: i18n.t('submission.table.partiposterTagsLabel', 'Partiposter Tags'),
                      width: 240,
                      minWidth: 200,
                      flex: 1,
                      renderCell: (props: GridRenderCellParams) => <TagCell {...props} />,
                      sortable: false,
                  },
              ]
            : []),
    ])
}

export const getRows = (
    status: REGISTRATION_STATUS_ENUM,
    registrations: RegistrationResultState[],
    profileById: Record<string, PartiposterProfile>,
    campaign: CampaignState,
): DeclinedRegistrationResultsTableRows[] => {
    return registrations.map(registration => {
        const profile = profileById[registration.partiposterId]
        const {
            submission_uuid,
            unmet_requirement_types,
            first_content_submitted_at,
            approval_expired_at,
            reviewed_by,
            approved_at,
        } = registration
        const { social_media_accounts: socialMediaAccounts } = profile
        const igProfile = socialMediaAccounts.filter(
            (account: SocialMediaAccount) => account.platform === PLATFORM_ENUM.IG,
        )[0]
        const fbProfile = socialMediaAccounts.filter(
            (account: SocialMediaAccount) => account.platform === PLATFORM_ENUM.FB,
        )[0]
        const tiktokProfile = socialMediaAccounts.filter(
            (account: SocialMediaAccount) => account.platform === PLATFORM_ENUM.TIKTOK,
        )[0]
        const twitterProfile = socialMediaAccounts.filter(
            (account: SocialMediaAccount) => account.platform === PLATFORM_ENUM.TWITTER,
        )[0]
        const timestamp = registration[TIMESTAMP_KEY[status]]
        const locale = getLanguageCode()
        const expiryDate = approval_expired_at
            ? getDurationInDaysAndHours(approval_expired_at, locale ? locale : 'en', campaign.country)
            : null
        const expiryDays = getDurationByDays(approval_expired_at, campaign.country)
        const hasRequirements = campaign.requirements.length > 0

        return {
            id: registration.uuid,
            uuid: registration.uuid,
            profile: {
                unmetRequirement: unmet_requirement_types,
                profile,
                hasRequirements,
            },
            socialMediaInfo: {
                profiles: [igProfile, fbProfile, tiktokProfile, twitterProfile],
            },
            approval_expired_at: {
                value: expiryDate ? expiryDate : '',
                color:
                    expiryDays >= 3
                        ? theme.colors.jungleGreen
                        : expiryDays >= 1
                          ? theme.colors.orange
                          : theme.colors.monza,
            },
            [TIMESTAMP_KEY[status]]: {
                value: timestamp ? parseBackendDate(timestamp, campaign.country, { showTime: true }) : '',
            },
            first_content_submitted_at: {
                value: first_content_submitted_at
                    ? parseBackendDate(first_content_submitted_at, campaign.country, { showTime: true })
                    : i18n.t('submission.table.notYetStartedTaskContent', 'Not yet'),
                color: first_content_submitted_at ? undefined : theme.colors.silver,
            },
            lastReviewedBy: {
                value: reviewed_by?.name,
            },
            lastReviewedAt: {
                value: approved_at ? parseBackendDate(approved_at, campaign.country, { showTime: true }) : undefined,
            },
            partiposterTags: {
                profileData: profile,
                submissionUuid: submission_uuid,
                campaignUuid: campaign.uuid,
            },
        }
    })
}
